import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Box, Button, Typography } from "@mui/material";
import { ISyncQueueItem } from "@home-health-notify/models";
import ProgressLoader from "../loader/ProgressLoader";
import {
  ListingTableCell,
  ListingTableContainer,
  ListingTableLayout,
  PatientName,
} from "../common/ListingTable.style";
import { EmptyState } from "./EmrPatients.style";
import { formatDateTime, patientName, servicesSummary } from "./syncQueueUtils";

const columns = [
  { id: "patient", label: "Patient", width: "40%" },
  { id: "mrn", label: "MRN", width: "18%" },
  { id: "created", label: "Received", width: "18%" },
  { id: "actions", label: "Actions", width: "24%" },
];

type NeedsReviewTableProps = {
  items: ISyncQueueItem[];
  isLoading: boolean;
  actingId: string | null;
  onRowClick: (item: ISyncQueueItem) => void;
  onApprove: (item: ISyncQueueItem) => void;
  onReject: (item: ISyncQueueItem) => void;
};

export function NeedsReviewTable({
  items,
  isLoading,
  actingId,
  onRowClick,
  onApprove,
  onReject,
}: NeedsReviewTableProps) {
  if (!isLoading && items.length === 0) {
    return (
      <EmptyState data-testid="needs-review-empty">
        <Typography variant="h6">All caught up</Typography>
        <Typography variant="body2">
          No patients are waiting for review right now. New patients from your
          EMR will appear here as they sync.
        </Typography>
      </EmptyState>
    );
  }

  return (
    <ListingTableLayout>
      <ListingTableContainer>
        <Table stickyHeader data-testid="needs-review-table">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell
                  key={column.id}
                  style={{ width: column.width, padding: "10px" }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <ProgressLoader />
                </TableCell>
              </TableRow>
            ) : (
              items.map(item => (
                <TableRow
                  key={item._id}
                  hover
                  sx={{ cursor: "pointer" }}
                  data-testid="needs-review-row"
                  onClick={() => onRowClick(item)}
                >
                  <ListingTableCell data-testid="patient-name">
                    <PatientName noWrap>{patientName(item)}</PatientName>
                    <Typography variant="caption" color="text.secondary">
                      {servicesSummary(item)}
                    </Typography>
                  </ListingTableCell>
                  <ListingTableCell data-testid="mrn">
                    {item.emrId}
                  </ListingTableCell>
                  <ListingTableCell>
                    {formatDateTime(item.createdAt)}
                  </ListingTableCell>
                  <ListingTableCell>
                    <Box
                      sx={{ display: "flex", gap: 1 }}
                      onClick={e => e.stopPropagation()}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        disabled={actingId !== null}
                        onClick={() => onApprove(item)}
                        data-testid="approve-button"
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        disabled={actingId !== null}
                        onClick={() => onReject(item)}
                        data-testid="reject-button"
                      >
                        Reject
                      </Button>
                    </Box>
                  </ListingTableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ListingTableContainer>
    </ListingTableLayout>
  );
}
